'use client';

// Needs its own client boundary: it uses hooks (the marquee pointer-events observer below) and
// MainPage — its only caller — is a Server Component, so it no longer inherits one.
import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import { getImageUrl } from '@/lib/imageUrl';
import { Checked as Icon_Checked } from "@/components/customer/icons/Checked";
const SvgIcons = { Checked: Icon_Checked };
import Link from 'next/link';

const SolutionsSection: React.FC = () => {
  const sliderUpRef = useRef<HTMLDivElement>(null);
  const sliderDownRef = useRef<HTMLDivElement>(null);
  const marqueeWindowRef = useRef<HTMLDivElement>(null);

  const productImages1 = getImageUrl(
    "/vilvabusiness/website/common/homepage/vilvad2cp_1.webp"
  );

  const productImages2 = getImageUrl(
    "/vilvabusiness/website/common/homepage/vilvawhatsease_1.webp"
  );

  const productImages3 = getImageUrl(
    "/vilvabusiness/website/common/homepage/vilvaone_1_1.webp"
  );
  const productImages4 = getImageUrl(
    "/vilvabusiness/website/common/homepage/wapro_1.webp"
  );

  const productImages5 = getImageUrl(
    "/vilvabusiness/website/common/homepage/pushwault_1.webp"
  );

  const productImages6 = getImageUrl(
    "/vilvabusiness/website/common/homepage/growsuit_1.webp"
  );

  const productImages7 = getImageUrl(
    "/vilvabusiness/website/common/homepage/verify_api_1.webp"
  );


  const productImages8 = getImageUrl(
    "/vilvabusiness/website/common/homepage/busbooking_1.webp"
  );

  // Single product list for both marquee columns. `href` turns a card into a crawlable internal
  // link to its money page (the homepage previously passed zero link equity to product pages).
  const PRODUCTS = [
    { src: productImages5, title: 'Push Vault', desc: 'Stay updated instantly with real-time product notifications – right in your browser.' },
    { src: productImages6, title: 'Vilva Growsuite', desc: 'Simplify workforce management with our all-in-one HRM solution.', href: '/growsuite' },
    { src: productImages7, title: 'Verification API', desc: 'Effortlessly verify user identities with our secure and reliable API.', href: '/verification-api' },
    { src: productImages1, title: 'Vilva D2C', desc: 'Streamline your online business with our all-in-one eCommerce SaaS solution.', href: '/d2c-e-commerce-platform-for-growth-stage-businesses' },
    { src: productImages2, title: 'Vilva Chat - Official Business WhatsApp API', desc: 'Send, automate, and manage customer conversations on the official WhatsApp Business API.', href: '/vilvachat-official-whatsapp-business-api' },
    { src: productImages3, title: 'VilvaOne', desc: 'Book your bus tickets & order your groceries within a second.', href: '/vilva-one' },
    { src: productImages4, title: 'Vilva Wapro', desc: 'Powerful WhatsApp marketing and broadcast automation for growing businesses.' },
    { src: productImages6, title: 'Vilva Growsuite', desc: 'Simplify workforce management with our all-in-one HRM solution.', href: '/growsuite' },
    { src: productImages7, title: 'Verification API', desc: 'Effortlessly verify user identities with our secure and reliable API.', href: '/verification-api' },
    { src: productImages1, title: 'Vilva D2C', desc: 'Streamline your online business with our all-in-one eCommerce SaaS solution.', href: '/d2c-e-commerce-platform-for-growth-stage-businesses' },
    { src: productImages2, title: 'Vilva Chat - Official Business WhatsApp API', desc: 'Send, automate, and manage customer conversations on the official WhatsApp Business API.', href: '/vilvachat-official-whatsapp-business-api' },
    { src: productImages3, title: 'VilvaOne', desc: 'Book your bus tickets & order your groceries within a second.', href: '/vilva-one' },
    { src: productImages8, title: 'Vilva Bus Booking', desc: 'Real-time bus search, live seat selection, instant booking, and secure payment management.', href: '/vilva-one' },
    { src: productImages5, title: 'Push Vault', desc: 'Stay updated instantly with real-time product notifications – right in your browser.' },
    { src: productImages4, title: 'Vilva Wapro', desc: 'Powerful WhatsApp marketing and broadcast automation for growing businesses.' },
  ];

  const renderCard = (item: any, index: number, prefix: string) => {
    // mb-8 (not mb-6) on mobile specifically: only one marquee column renders below `md:`
    // (the second is `hidden md:block`), so every card stacks in a single continuously
    // scrolling column there — the extra gap keeps consecutive cards from reading as one
    // cramped touch target during the animation. Desktop's 2-column layout is unaffected.
    // `data-marquee-card` is the observer's hook for gating each card's pointer events and
    // tab stop to the window it is scrolling through — see the second effect below.
    // Linked and unlinked cards used to share one className, hover:shadow-md included, so the
    // three entries without an href (Push Vault, Vilva Wapro) lifted on hover exactly like a
    // real link and then did nothing when clicked. The hover cue now belongs to actual links.
    const base = "rounded-[12px] mb-8 md:mb-6 bg-white flex flex-col items-start gap-[10px] p-[16px]";
    const cls = item.href
      ? `${base} transition-shadow hover:shadow-md cursor-pointer`
      : `${base} cursor-default`;
    const inner = (
      <>
        <Image className="w-10 h-10" src={item.src} alt={item.title} width={40} height={40} />
        <p className="text-[#090D28] text-[16px] font-semibold schibsted-grotesk leading-none p-0 m-0">
          {item.title}
        </p>
        <p className="text-[#727272] text-[13px] font-normal leading-[140%] schibsted-grotesk">
          {item.desc}
        </p>
      </>
    );
    return item.href ? (
      <Link key={`${prefix}-${index}`} href={item.href} title={item.title} className={cls} data-marquee-card="">
        {inner}
      </Link>
    ) : (
      <div key={`${prefix}-${index}`} className={cls} data-marquee-card="">
        {inner}
      </div>
    );
  };

  // Both columns scroll via CSS (see .marquee-track in globals.css) instead of a GSAP timeline.
  // GSAP's `y: "-50%"` and CSS `translateY(-50%)` resolve against the same box, and each column
  // renders the product list twice, so this is the same motion at the same 20s period — minus
  // downloading GSAP and building a timeline while the page hydrates.

  // Each column is translated by up to half its own height, so a card scrolled out of the 550px
  // window still has a bounding rect that sweeps over the content above and below this section —
  // including the "Explore Our Products" button. The overflow clip means those cards were never
  // really clickable out there, but nothing said so: axe/Lighthouse counted them as obscuring
  // touch targets (WCAG 2.5.8, "smallest space is 16px by 51.2px") and the keyboard could tab
  // into cards nobody can see. So the columns go `pointer-events: none` and each card opts back
  // in only while it is fully inside the window. The gate is applied here rather than in the
  // markup because it is this observer that lifts it again — without JS the cards stay clickable.
  useEffect(() => {
    const root = marqueeWindowRef.current;
    const columns = [sliderUpRef.current, sliderDownRef.current];
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-marquee-card]'));
    const setInteractive = (card: HTMLElement, on: boolean) => {
      card.classList.toggle('pointer-events-auto', on);
      if (card.tagName === 'A') card.tabIndex = on ? 0 : -1;
    };

    columns.forEach(column => column?.classList.add('pointer-events-none'));
    cards.forEach(card => setInteractive(card, false));

    // A partially visible card still pokes out of the window, so require it to be fully inside.
    const io = new IntersectionObserver(
      entries => entries.forEach(entry =>
        setInteractive(entry.target as HTMLElement, entry.intersectionRatio >= 0.99)
      ),
      { root, threshold: 0.99 }
    );
    cards.forEach(card => io.observe(card));

    return () => {
      io.disconnect();
      columns.forEach(column => column?.classList.remove('pointer-events-none'));
      cards.forEach(card => setInteractive(card, true));
    };
  }, []);

  return (
    <section className="bg-[#EBF0FF] py-[60px] md:py-[120px]">
      <div className="grid grid-cols-1 lg:grid-cols-2 max-w-7xl md:container">
        <div className="px-4 md:px-0  md:container">
          <h2 className="text-[32px] md:text-[48px] font-extrabold text-[#090D28] font-NeueMontreal">
            Our{' '}
            <span className="text-[32px] md:text-[48px] text-transparent bg-clip-text font-NeueMontreal bg-[linear-gradient(90deg,_#1557FF_28.37%,_#29ACE8_100%)] font-normal leading-[120%]">
              Products & Services
            </span>
          </h2>
          <p className="schibsted-grotesk text-[#090D28]/70 text-[16px] md:text-[18px] font-[400] leading-[150%] mb-6 mt-4">
            Startups, small teams, or enterprises we’ve built it for everyone. One platform that scales with your business.
          </p>
          <ul className="m-0 p-0">
            <li className='flex md:items-center items-start mb-8  gap-2'>
              <p>
                <SvgIcons.Checked />
              </p>
              <p className='text-[#090D28] text-[16px] md:text-[18px] font-normal leading-[120%] schibsted-grotesk flex items-baseline '>
                Easy-to-use dashboard to manage everything in one place
              </p>
            </li>
            <li className='flex md:items-center items-start mb-8 gap-2'>
              <p>
                <SvgIcons.Checked />
              </p>
              <p className='text-[#090D28] text-[16px] md:text-[18px] font-normal leading-[120%] schibsted-grotesk flex items-baseline '>
                Automate WhatsApp communication & sales workflows
              </p>
            </li>
            <li className='flex md:items-center items-start mb-8 gap-2'>
              <p>
                <SvgIcons.Checked />
              </p>
              <p className='text-[#090D28] text-[16px] md:text-[18px] font-normal leading-[120%] schibsted-grotesk flex items-baseline '>
                Custom solutions, white-label options, and team training
              </p>
            </li>
            <li className='flex md:items-center items-start mb-8 gap-2'>
              <p>
                <SvgIcons.Checked />
              </p>
              <p className='text-[#090D28] text-[16px] md:text-[18px] font-normal leading-[120%] schibsted-grotesk flex items-baseline '>
                API integrations, advanced automation, and SLA support
              </p>
            </li>
            <li className='flex md:items-center items-start mb-8 gap-2'>
              <p>
                <SvgIcons.Checked />
              </p>
              <p className='text-[#090D28] text-[16px] md:text-[18px] font-normal leading-[120%] schibsted-grotesk flex items-baseline '>
                Add new modules like apps, websites, and CRM as you grow
              </p>
            </li>

          </ul>
          <Link aria-label="Explore Vilva Business products and services" title="Business Websites & Products"
            href="/business-websites"
            className="mt-6 mb-2 inline-flex min-h-[48px] px-[24px] py-[16px] justify-center items-center flex-shrink-0 text-[#195AFF] text-[16px] md:text-[18px] font-[500] leading-[120%] schibsted-grotesk rounded-[8px] bg-white shadow-[-11px_34px_10px_0px_rgba(25,90,255,0),-7px_21px_9px_0px_rgba(25,90,255,0.01),-4px_12px_8px_0px_rgba(25,90,255,0.05),-2px_5px_6px_0px_rgba(25,90,255,0.09),0px_1px_3px_0px_rgba(25,90,255,0.10)]"
          >
            Explore Our Products
          </Link>
        </div>

        <div className="container">
          <div ref={marqueeWindowRef} className="grid grid-cols-1 relative h-[550px] overflow-y-hidden gap-[16px] mt-[80px] lg:mt-0 xl:gap-[16px] sm:grid-cols-2 lg:grid-cols-2">
            {/* Decorative only — `pointer-events-none` keeps the fades from swallowing clicks on the cards beneath them. */}
            <div className="pointer-events-none flex-shrink-0 absolute top-0 z-20 w-full h-[80px] bg-[linear-gradient(180deg,_#EBF0FF_0%,_rgba(235,240,255,0)_100%)]"></div>
            <div className="pointer-events-none flex-shrink-0 absolute bottom-0 z-20 w-full h-[80px] bg-[linear-gradient(0deg,_#EBF0FF_0%,_rgba(235,240,255,0)_100%)]"></div>
            <div className="pointer-events-none w-[100px] left-[200px] top-[170px] hidden md:block absolute mx-auto h-[353px] flex-shrink-0 rounded-full bg-[#6DA4F7] blur-[120px]"></div>

            <div
              className="marquee-track"
              style={{ animation: 'marqueeUp 20s linear infinite' }}
              ref={sliderUpRef}
            >
              {PRODUCTS.map((item, index) => renderCard(item, index, 'up'))}
            </div>

            <div
              className="marquee-track hidden md:block"
              style={{ animation: 'marqueeDown 20s linear infinite' }}
              ref={sliderDownRef}
            >
              {PRODUCTS.map((item, index) => renderCard(item, index, 'down'))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;